import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';

const CATEGORIES = ['News', 'Events', 'Announcements', 'Success Stories', 'Community'];
const EMPTY_FORM  = { title: '', content: '', category: 'News', isFeatured: false };

// ── Compress + convert image file to base64 string ───────────────
const imageFileToBase64 = (file, maxWidth = 900, quality = 0.78) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.onload = () => {
        // Scale down if wider than maxWidth
        let w = img.width;
        let h = img.height;
        if (w > maxWidth) {
          h = Math.round((h * maxWidth) / w);
          w = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width  = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);

        // Return full base64 data URL (e.g. "data:image/jpeg;base64,...")
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const AdminPosts = () => {
  const [posts, setPosts]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [editingPost, setEditingPost]     = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [imageFiles, setImageFiles]       = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [saving, setSaving]               = useState(false);
  const [saveStatus, setSaveStatus]       = useState('');
  const [deleteModal, setDeleteModal]     = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const fileInputRef                      = useRef();
  const { success, error, warning }       = useToast();

  // ── Fetch posts ───────────────────────────────────────────────
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/posts');
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // ── Image selection ───────────────────────────────────────────
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const valid = files.filter(f => {
      if (!f.type.startsWith('image/')) {
        error(`${f.name} is not an image`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        error(`${f.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    setImageFiles(prev => [...prev, ...valid]);
    setImagePreviews(prev => [
      ...prev,
      ...valid.map(f => URL.createObjectURL(f)),
    ]);
    e.target.value = '';
  };

  const removeNewImage = (i) => {
    URL.revokeObjectURL(imagePreviews[i]);
    setImageFiles(prev    => prev.filter((_, idx) => idx !== i));
    setImagePreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const removeExistingImage = (indexToRemove) => {
    setEditingPost(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== indexToRemove),
    }));
  };

  // ── Open / close form ─────────────────────────────────────────
  const openCreate = () => {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setImageFiles([]);
    setImagePreviews([]);
    setSaveStatus('');
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingPost({ ...post }); // clone so we can mutate images array
    setForm({
      title:      post.title      || '',
      content:    post.content    || '',
      category:   post.category   || 'News',
      isFeatured: post.isFeatured || false,
    });
    setImageFiles([]);
    setImagePreviews([]);
    setSaveStatus('');
    setShowForm(true);
  };

  const closeForm = () => {
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setShowForm(false);
    setSaveStatus('');
  };

  // ── Submit ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim())   { error('Please enter a title');   return; }
    if (!form.content.trim()) { error('Please enter the content'); return; }

    setSaving(true);

    try {
      // ── Step 1: Compress & convert new images to base64 ──
      let newBase64Images = [];

      if (imageFiles.length > 0) {
        setSaveStatus(`Processing ${imageFiles.length} image(s)...`);

        newBase64Images = await Promise.all(
          imageFiles.map((file, i) => {
            setSaveStatus(`Compressing image ${i + 1} of ${imageFiles.length}...`);
            return imageFileToBase64(file);
          })
        );
      }

      // ── Step 2: Merge existing + new images ──
      const existingImages = editingPost?.images || [];
      const allImages      = [...existingImages, ...newBase64Images];

      // ── Step 3: Save to Firestore via backend ──
      setSaveStatus('Saving post...');

      const payload = {
        title:      form.title.trim(),
        content:    form.content.trim(),
        category:   form.category,
        isFeatured: form.isFeatured === true || form.isFeatured === 'true',
        images:     allImages, // base64 strings + any existing URLs
      };

      if (editingPost) {
        await api.put(`/posts/${editingPost.id}`, payload);
        success('✅ Post updated successfully!');
      } else {
        await api.post('/posts', payload);
        success('🎉 Post published successfully!');
      }

      closeForm();
      fetchPosts();

    } catch (err) {
      console.error('Post save error:', err);
      const status = err.status || err.response?.status;
      if (status === 401) {
        error('Session expired. Please log in again.');
      } else if (status === 403) {
        error('Admin access required to publish posts.');
      } else {
        error(err.message || 'Failed to save post. Please try again.');
      }
    } finally {
      setSaving(false);
      setSaveStatus('');
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/posts/${deleteModal.id}`);
      success('Post deleted successfully');
      setDeleteModal(null);
      fetchPosts();
    } catch (err) {
      error('Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────
  const formatDate = (val) => {
    if (val?.seconds) return new Date(val.seconds * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    if (typeof val === 'string') return new Date(val).toLocaleDateString('en-IN');
    return '—';
  };

  // Resolve image for display — handles base64, full URLs, /uploads paths
  const resolveImage = (src) => {
    if (!src) return null;
    if (src.startsWith('data:'))  return src;                          // base64
    if (src.startsWith('http'))   return src;                          // full URL
    if (src.startsWith('/'))      return `http://localhost:5000${src}`; // local
    return src;
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <AdminLayout title="News & Posts">

      {/* Header */}
      <div className="ap-header">
        <p className="ap-count">{posts.length} post{posts.length !== 1 ? 's' : ''} total</p>
        <button className="btn btn-primary" onClick={openCreate}>+ New Post</button>
      </div>

      {/* Table */}
      <div className="admin-card">
        {loading ? (
          <div className="ap-center">
            <div className="ap-spinner" />
            <p>Loading posts...</p>
          </div>
        ) : posts.length > 0 ? (
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Featured</th>
                  <th>Images</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <div className="ap-title">{post.title}</div>
                      <div className="ap-preview">
                        {post.content?.replace(/<[^>]*>/g, '').substring(0, 70)}...
                      </div>
                    </td>
                    <td>
                      <span className="tag tag--news">{post.category || 'News'}</span>
                    </td>
                    <td>
                      {post.isFeatured
                        ? <span className="tag tag--featured">⭐ Yes</span>
                        : <span className="ap-dash">—</span>}
                    </td>
                    <td>
                      <span className="ap-img-count">
                        {post.images?.length || 0} 🖼️
                      </span>
                    </td>
                    <td className="ap-date">{formatDate(post.createdAt)}</td>
                    <td>
                      <div className="ap-actions">
                        <button className="ap-btn ap-btn--edit"   onClick={() => openEdit(post)}>✏️ Edit</button>
                        <button className="ap-btn ap-btn--delete" onClick={() => setDeleteModal(post)}>🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ap-center">
            <div style={{ fontSize: '3.5rem', marginBottom: 'var(--space-4)' }}>📰</div>
            <h3 style={{ color: 'var(--neutral-800)', marginBottom: 'var(--space-2)' }}>No posts yet</h3>
            <p style={{ color: 'var(--neutral-500)', marginBottom: 'var(--space-5)' }}>
              Create your first news post to get started
            </p>
            <button className="btn btn-primary" onClick={openCreate}>+ Create First Post</button>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div
          className="ap-overlay"
          onClick={(e) => e.target === e.currentTarget && !saving && closeForm()}
        >
          <div className="ap-modal">

            {/* Modal header */}
            <div className="ap-modal__hdr">
              <div>
                <h2>{editingPost ? '✏️ Edit Post' : '✍️ New Post'}</h2>
                <p className="ap-modal__sub">
                  Images are stored directly in Firestore
                </p>
              </div>
              <button
                className="ap-modal__close"
                onClick={closeForm}
                disabled={saving}
                type="button"
              >✕</button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="ap-form" noValidate>

              {/* Title */}
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  className="form-input"
                  required
                  value={form.title}
                  onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Enter post title"
                  disabled={saving}
                />
              </div>

              {/* Category + Featured */}
              <div className="ap-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={form.category}
                    onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
                    disabled={saving}
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Featured on Homepage?</label>
                  <select
                    className="form-select"
                    value={form.isFeatured ? 'yes' : 'no'}
                    onChange={(e) => setForm(p => ({ ...p, isFeatured: e.target.value === 'yes' }))}
                    disabled={saving}
                  >
                    <option value="no">No</option>
                    <option value="yes">⭐ Yes — Show on Homepage</option>
                  </select>
                </div>
              </div>

              {/* Content */}
              <div className="form-group">
                <label className="form-label">Content *</label>
                <textarea
                  className="form-textarea"
                  rows={8}
                  required
                  style={{ minHeight: '200px' }}
                  value={form.content}
                  onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
                  placeholder="Write your post content here..."
                  disabled={saving}
                />
              </div>

              {/* Images */}
              <div className="form-group">
                <label className="form-label">
                  Images
                  {imageFiles.length > 0 && (
                    <span className="img-badge">{imageFiles.length} selected</span>
                  )}
                  <span className="ap-img-hint">Stored directly in Firestore • Max 10MB each</span>
                </label>

                {/* Drop zone */}
                <div
                  className={`ap-dropzone ${saving ? 'ap-dropzone--disabled' : ''}`}
                  onClick={() => !saving && fileInputRef.current?.click()}
                >
                  <span style={{ fontSize: '2rem' }}>📷</span>
                  <p>Click to select images (multiple allowed)</p>
                  <small>JPG, PNG, WebP — compressed automatically before saving</small>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  multiple
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                  disabled={saving}
                />

                {/* New image previews */}
                {imagePreviews.length > 0 && (
                  <div className="ap-img-section">
                    <p className="ap-img-label">
                      New images ({imagePreviews.length}) — will be compressed &amp; saved:
                    </p>
                    <div className="ap-previews">
                      {imagePreviews.map((src, i) => (
                        <div key={i} className="ap-thumb">
                          <img src={src} alt="" />
                          {!saving && (
                            <button
                              type="button"
                              className="ap-thumb__rm"
                              onClick={() => removeNewImage(i)}
                              title="Remove"
                            >✕</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Existing images from Firestore */}
                {editingPost?.images?.length > 0 && (
                  <div className="ap-img-section">
                    <p className="ap-img-label">
                      Existing images ({editingPost.images.length}) — click ✕ to remove:
                    </p>
                    <div className="ap-previews">
                      {editingPost.images.map((src, i) => (
                        <div key={i} className="ap-thumb">
                          <img
                            src={resolveImage(src)}
                            alt=""
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {!saving && (
                            <button
                              type="button"
                              className="ap-thumb__rm"
                              onClick={() => removeExistingImage(i)}
                              title="Remove"
                            >✕</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status indicator */}
              {saveStatus && (
                <div className="ap-status">
                  <div className="ap-status__spinner" />
                  <span>{saveStatus}</span>
                </div>
              )}

              {/* Footer */}
              <div className="ap-form-footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeForm}
                  disabled={saving}
                >Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <span className="ap-btn-loading">
                      <span className="ap-btn-spinner" />
                      {saveStatus || 'Saving...'}
                    </span>
                  ) : editingPost ? '✅ Update Post' : '🚀 Publish Post'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={handleDelete}
        title="Delete Post"
        message={`Delete "${deleteModal?.title}"? This cannot be undone.`}
        confirmText="Delete"
        type="danger"
        loading={deleting}
      />

      <style jsx>{`
        /* Header */
        .ap-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-6); }
        .ap-count  { color:var(--neutral-600); font-size:var(--text-sm); margin:0; }

        /* Card */
        .admin-card { background:white; border-radius:var(--radius-xl); padding:var(--space-6); border:1px solid var(--neutral-200); box-shadow:var(--shadow-sm); }

        /* Table */
        .ap-table-wrap { overflow-x:auto; }
        .ap-table { width:100%; border-collapse:collapse; font-size:var(--text-sm); }
        .ap-table th { text-align:left; padding:var(--space-3) var(--space-4); background:var(--neutral-50); color:var(--neutral-700); font-weight:var(--weight-semibold); border-bottom:2px solid var(--neutral-200); font-size:var(--text-xs); text-transform:uppercase; letter-spacing:0.5px; white-space:nowrap; }
        .ap-table td { padding:var(--space-4); border-bottom:1px solid var(--neutral-200); vertical-align:middle; }
        .ap-table tr:last-child td { border-bottom:none; }
        .ap-table tr:hover td { background:var(--neutral-50); }
        .ap-title   { font-weight:var(--weight-semibold); color:var(--neutral-900); margin-bottom:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:260px; }
        .ap-preview { font-size:var(--text-xs); color:var(--neutral-500); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:260px; }
        .ap-date    { font-size:var(--text-xs); color:var(--neutral-500); white-space:nowrap; }
        .ap-img-count { font-size:var(--text-sm); color:var(--neutral-600); }
        .ap-dash    { color:var(--neutral-300); }

        .ap-actions { display:flex; gap:var(--space-2); }
        .ap-btn { padding:var(--space-1) var(--space-3); border:none; border-radius:var(--radius-md); font-size:var(--text-xs); font-weight:var(--weight-semibold); cursor:pointer; transition:all var(--transition-fast); font-family:var(--font-body); white-space:nowrap; }
        .ap-btn--edit   { background:var(--primary-100); color:var(--primary-700); }
        .ap-btn--edit:hover   { background:var(--primary-200); }
        .ap-btn--delete { background:var(--error-light);   color:var(--error); }
        .ap-btn--delete:hover { background:#fecaca; }

        .tag { padding:2px var(--space-2); border-radius:var(--radius-full); font-size:10px; font-weight:var(--weight-bold); text-transform:uppercase; white-space:nowrap; }
        .tag--news     { background:var(--primary-100); color:var(--primary-700); }
        .tag--featured { background:var(--gold-100);    color:var(--gold-900);    }

        .ap-center { text-align:center; padding:var(--space-16); color:var(--neutral-500); }
        .ap-spinner { width:36px; height:36px; border:3px solid var(--neutral-200); border-top-color:var(--primary-500); border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto var(--space-4); }
        @keyframes spin { to { transform:rotate(360deg); } }

        /* Modal */
        .ap-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px); z-index:var(--z-modal); display:flex; align-items:flex-start; justify-content:center; padding:var(--space-6) var(--space-4); overflow-y:auto; }
        .ap-modal { background:white; border-radius:var(--radius-2xl); width:100%; max-width:780px; box-shadow:var(--shadow-xl); animation:slideUp 0.3s ease-out; margin:auto; }
        @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }

        .ap-modal__hdr { display:flex; justify-content:space-between; align-items:flex-start; padding:var(--space-6) var(--space-8); border-bottom:2px solid var(--primary-100); background:linear-gradient(135deg,var(--primary-50),var(--gold-100)); border-radius:var(--radius-2xl) var(--radius-2xl) 0 0; }
        .ap-modal__hdr h2 { font-size:var(--text-2xl); color:var(--primary-900); margin:0 0 var(--space-1); }
        .ap-modal__sub { font-size:var(--text-xs); color:var(--neutral-500); margin:0; }
        .ap-modal__close { background:rgba(0,0,0,0.08); border:none; width:34px; height:34px; border-radius:50%; cursor:pointer; font-size:var(--text-base); display:flex; align-items:center; justify-content:center; transition:all var(--transition-fast); color:var(--neutral-700); flex-shrink:0; }
        .ap-modal__close:hover:not(:disabled) { background:rgba(0,0,0,0.15); }
        .ap-modal__close:disabled { opacity:0.4; cursor:not-allowed; }

        .ap-form { padding:var(--space-8); display:flex; flex-direction:column; gap:var(--space-5); max-height:75vh; overflow-y:auto; }
        .ap-row { display:grid; grid-template-columns:1fr 1fr; gap:var(--space-4); }
        .form-group { display:flex; flex-direction:column; gap:var(--space-2); }

        /* Dropzone */
        .ap-dropzone { border:2px dashed var(--neutral-300); border-radius:var(--radius-lg); padding:var(--space-6); text-align:center; cursor:pointer; transition:all var(--transition-fast); display:flex; flex-direction:column; align-items:center; gap:var(--space-2); color:var(--neutral-600); background:var(--neutral-50); }
        .ap-dropzone:hover:not(.ap-dropzone--disabled) { border-color:var(--primary-400); background:var(--primary-50); color:var(--primary-600); }
        .ap-dropzone--disabled { opacity:0.5; cursor:not-allowed; }
        .ap-dropzone p { margin:0; font-size:var(--text-sm); font-weight:var(--weight-medium); }
        .ap-dropzone small { font-size:var(--text-xs); color:var(--neutral-400); }

        .img-badge { display:inline-block; margin-left:var(--space-2); background:var(--primary-100); color:var(--primary-700); font-size:10px; font-weight:var(--weight-bold); padding:2px var(--space-2); border-radius:var(--radius-full); }
        .ap-img-hint { display:block; font-size:var(--text-xs); color:var(--neutral-400); font-weight:var(--weight-normal); margin-top:2px; }

        .ap-img-section { margin-top:var(--space-4); }
        .ap-img-label { font-size:var(--text-xs); color:var(--neutral-500); font-weight:var(--weight-medium); margin-bottom:var(--space-2); }
        .ap-previews { display:flex; gap:var(--space-3); flex-wrap:wrap; }
        .ap-thumb { position:relative; width:80px; height:80px; border-radius:var(--radius-lg); overflow:hidden; border:2px solid var(--neutral-200); flex-shrink:0; background:var(--neutral-100); }
        .ap-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
        .ap-thumb__rm { position:absolute; top:3px; right:3px; width:20px; height:20px; border-radius:50%; background:rgba(0,0,0,0.75); border:none; color:white; font-size:10px; cursor:pointer; display:flex; align-items:center; justify-content:center; line-height:1; }
        .ap-thumb__rm:hover { background:var(--error); }

        /* Status */
        .ap-status { display:flex; align-items:center; gap:var(--space-3); background:var(--primary-50); border:1px solid var(--primary-200); border-radius:var(--radius-lg); padding:var(--space-3) var(--space-4); font-size:var(--text-sm); color:var(--primary-700); font-weight:var(--weight-medium); }
        .ap-status__spinner { width:16px; height:16px; border:2px solid var(--primary-200); border-top-color:var(--primary-500); border-radius:50%; animation:spin 0.8s linear infinite; flex-shrink:0; }

        /* Footer */
        .ap-form-footer { display:flex; gap:var(--space-3); justify-content:flex-end; padding-top:var(--space-5); border-top:1px solid var(--neutral-200); }
        .ap-btn-loading { display:flex; align-items:center; gap:var(--space-2); }
        .ap-btn-spinner { width:14px; height:14px; border:2px solid rgba(255,255,255,0.3); border-top-color:white; border-radius:50%; animation:spin 0.8s linear infinite; }

        @media (max-width:600px) {
          .ap-row { grid-template-columns:1fr; }
          .ap-form { padding:var(--space-4); }
          .ap-modal__hdr { padding:var(--space-4) var(--space-5); }
          .ap-form-footer { flex-direction:column-reverse; }
          .ap-form-footer .btn { width:100%; justify-content:center; }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminPosts;